import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, IconButton } from '@mui/material';

import { useShallow } from 'zustand/react/shallow';

import {
  Container,
  FileCardContainer,
  FileList,
  FileNameText,
  FileRow,
  FileThumbnail,
  Header,
  HeaderLeft,
  HeaderRight,
  HeaderTitle,
  OpenButton,
  ProgressFill,
  ProgressTrack,
  ThumbnailLine,
} from '@/app/ui/UploadProgress/UploadProgress.styles';
import UploadSpinner from '@/app/ui/UploadProgress/UploadSpinner';
import { ReactComponent as LinkExternalIcon } from '@/assets/icons/link-external.svg';
import { ReactComponent as SuccessIcon } from '@/assets/icons/snackbar-success.svg';
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/upload-chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '@/assets/icons/upload-chevron-up.svg';
import { ReactComponent as CloseIcon } from '@/assets/icons/upload-close.svg';
import useUploadedFilesStore, { FileMetadata } from '@/shared/api/uploadedFiles';

const THUMBNAIL_LINES: { width: string; visible: boolean }[] = [
  { width: '0.4375rem', visible: true },
  { width: '1.4375rem', visible: true },
  { width: '0.625rem', visible: true },
  { width: '0.5625rem', visible: false },
  { width: '1.125rem', visible: true },
  { width: '0.8125rem', visible: true },
  { width: '0.4375rem', visible: true },
  { width: '0.4375rem', visible: true },
  { width: '0.4375rem', visible: false },
  { width: '1.1875rem', visible: true },
  { width: '0.9375rem', visible: true },
  { width: '1rem', visible: true },
];

const iconButtonSx = {
  padding: 0,
  width: '1.25rem',
  height: '1.25rem',
  '&:hover': { backgroundColor: 'transparent', opacity: 0.7 },
};

const FileDocumentIcon: FC = () => (
  <FileThumbnail>
    {THUMBNAIL_LINES.map((line, i) => (
      <ThumbnailLine key={i} width={line.width} visible={line.visible} />
    ))}
  </FileThumbnail>
);

interface FileCardProps {
  file: FileMetadata;
  onOpenFile: (fileName: string) => void;
}

const FileCard: FC<FileCardProps> = ({ file, onOpenFile }) => {
  const { t } = useTranslation();
  const isUploaded = file.progress >= 100;

  const handleOpen = () => onOpenFile(file.name);

  return (
    <FileCardContainer>
      <FileRow>
        {file.url ? (
          <Box
            component="img"
            src={file.url}
            sx={{
              width: '2.25rem',
              height: '3.1875rem',
              borderRadius: '0.25rem',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <FileDocumentIcon />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FileNameText>{file.name}</FileNameText>
        </Box>
        <OpenButton onClick={handleOpen}>
          {t('open_file')}
          <LinkExternalIcon width="14" height="14" />
        </OpenButton>
      </FileRow>
      <ProgressTrack>
        <ProgressFill progress={isUploaded ? 100 : file.progress} />
      </ProgressTrack>
    </FileCardContainer>
  );
};

const UploadProgress: FC = () => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { files, clearFiles, requestFileOpen } = useUploadedFilesStore(
    useShallow((state) => ({
      files: state.files,
      clearFiles: state.clearFiles,
      requestFileOpen: state.requestFileOpen,
    })),
  );

  const handleOpenFile = useCallback(
    (fileName: string) => {
      requestFileOpen(fileName);
    },
    [requestFileOpen],
  );

  const allFilesUploaded = useMemo(
    () => files.length > 0 && files.every((f) => f.progress >= 90),
    [files],
  );

  const minProgress = useMemo(
    () => (files.length > 0 ? Math.min(...files.map((f) => f.progress)) : 0),
    [files],
  );

  const title = useMemo(() => {
    if (allFilesUploaded) {
      return t(files.length === 1 ? 'uploaded_one_file' : 'uploaded_files_count', {
        count: files.length,
      });
    }
    return t(files.length === 1 ? 'uploading_one_file' : 'uploading_files_count', {
      count: files.length,
    });
  }, [allFilesUploaded, files.length, t]);

  useEffect(() => {
    if (!allFilesUploaded) return;

    const timeoutId = setTimeout(() => {
      clearFiles();
    }, 10_000);

    return () => clearTimeout(timeoutId);
  }, [allFilesUploaded, clearFiles]);

  const containerRef = useRef<HTMLDivElement>(null);

  const updateCssVariable = useCallback(() => {
    const height = containerRef.current?.offsetHeight ?? 0;
    document.documentElement.style.setProperty(
      '--upload-progress-offset',
      height > 0 ? `${height + 8}px` : '0px',
    );
  }, []);

  useEffect(() => {
    if (!files.length) {
      document.documentElement.style.setProperty('--upload-progress-offset', '0px');
      return;
    }

    updateCssVariable();

    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(updateCssVariable);
    observer.observe(el);
    return () => observer.disconnect();
  }, [files.length, isCollapsed, updateCssVariable]);

  if (!files.length) return null;

  return (
    <Container ref={containerRef} sx={{ gap: isCollapsed ? 0 : '1rem' }}>
      <Header>
        <HeaderLeft>
          {allFilesUploaded ? (
            <Box sx={{ width: '1.5rem', height: '1.5rem', flexShrink: 0 }}>
              <SuccessIcon width="24" height="24" />
            </Box>
          ) : (
            <UploadSpinner progress={minProgress} />
          )}
          <HeaderTitle>{title}</HeaderTitle>
        </HeaderLeft>
        <HeaderRight>
          <IconButton onClick={() => setIsCollapsed((prev) => !prev)} sx={iconButtonSx}>
            {isCollapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </IconButton>
          <IconButton onClick={clearFiles} sx={iconButtonSx}>
            <CloseIcon />
          </IconButton>
        </HeaderRight>
      </Header>

      {!isCollapsed && (
        <FileList>
          {files.map((file) => (
            <FileCard key={file.id} file={file} onOpenFile={handleOpenFile} />
          ))}
        </FileList>
      )}
    </Container>
  );
};

export default UploadProgress;
